// Which language a URL is in, and how a link is written for a language.
// Pure, so the rules are unit-tested (languagePath.test.ts); the
// useLanguageRouter hook, the router in App.tsx, useSEO and the language
// banner all read the language from the path through here.
//
// Two kinds of language (shared/pageLanguages.ts):
//  - the 14 site languages: /{lang}/anything is that page in that language;
//  - page languages (Thai, Traditional Chinese): /{code}{path} counts only
//    for the pages the language has. On any other path the prefix is
//    dropped: the router replaces /th/pricing with /pricing, and a link from
//    a Thai page to /pricing is written without the prefix.
import { supportedLanguages } from "@/locales";
import {
  getPageLanguage,
  isPageLanguagePath,
  splitPageLanguagePath,
} from "@shared/pageLanguages";

const EXTERNAL_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:/i;

export function splitPath(path: string) {
  let pathname = path || "/";
  let search = "";
  let hash = "";

  const hashIndex = pathname.indexOf("#");
  if (hashIndex >= 0) {
    hash = pathname.slice(hashIndex);
    pathname = pathname.slice(0, hashIndex);
  }

  const queryIndex = pathname.indexOf("?");
  if (queryIndex >= 0) {
    search = pathname.slice(queryIndex);
    pathname = pathname.slice(0, queryIndex);
  }

  if (!pathname) {
    pathname = "/";
  }

  return { pathname, search, hash };
}

/**
 * The language of a pathname and the pathname without its prefix.
 * `strayPageLanguage`: the path carries a page language's prefix but is not
 * one of its pages (/th/pricing); it reads as the English page, and the
 * router redirects it to `pathname`.
 */
export function stripLanguagePrefix(pathname: string) {
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = normalizedPath.split("/").filter(Boolean);
  const potentialLang = segments[0];

  if (potentialLang && supportedLanguages.includes(potentialLang as any)) {
    const remainingPath = segments.slice(1).join("/");
    return {
      lang: potentialLang as string,
      pathname: remainingPath ? `/${remainingPath}` : "/",
      hasLang: true,
      strayPageLanguage: false,
    };
  }

  const pageLanguage = splitPageLanguagePath(normalizedPath);
  if (pageLanguage) {
    return {
      lang: pageLanguage.ownPage ? pageLanguage.code : "en",
      pathname: pageLanguage.basePath,
      hasLang: pageLanguage.ownPage,
      strayPageLanguage: !pageLanguage.ownPage,
    };
  }

  return {
    lang: "en",
    pathname: normalizedPath || "/",
    hasLang: false,
    strayPageLanguage: false,
  };
}

/** The language a location (path with optional query and hash) is in. */
export function languageOfPath(location: string): string {
  return stripLanguagePrefix(splitPath(location).pathname).lang;
}

/**
 * Where a URL with a page language's prefix on a page that language does not
 * have should go instead: the same path, query and hash without the prefix.
 * Null for every other URL.
 */
export function strayPageLanguageTarget(location: string): string | null {
  const page = splitPageLanguagePath(location);
  return page && !page.ownPage ? page.rest : null;
}

function isExternal(path: string) {
  return EXTERNAL_SCHEME_REGEX.test(path) || path.startsWith("//");
}

/**
 * `path` as a link for `lang`: English has no prefix, a site language
 * prefixes every path, a page language prefixes only its own pages.
 */
export function withLanguagePrefix(path: string, lang: string): string {
  if (!path) {
    return lang === "en" || getPageLanguage(lang) ? "/" : `/${lang}`;
  }

  if (isExternal(path) || path.startsWith("#")) {
    return path;
  }

  const { pathname: rawPathname, search, hash } = splitPath(path);
  const { pathname: cleanPathname } = stripLanguagePrefix(rawPathname);
  const linkLang =
    getPageLanguage(lang) && !isPageLanguagePath(lang, cleanPathname)
      ? "en"
      : lang;

  const normalizedPath =
    linkLang === "en"
      ? cleanPathname
      : cleanPathname === "/"
        ? `/${linkLang}`
        : `/${linkLang}${cleanPathname}`;

  return `${normalizedPath}${search}${hash}`;
}
